/**
 * georeferencing.ts
 * ジオリファレンス・ワールドファイル生成関連メソッド
 * App.vue から切り出し
 */

import {
  imageCssToNatural,
  lngLatToMerc,
  fitSimilarity2PExactDown,
  fitAffineRobust,
  composeAffine,
  applyAffine,
  worldFileFromAffine,
} from '@/js/utils/app'

type VM = any
type AffineMatrix = [number, number, number, number, number, number]

export function _applyAffine(vm: VM, M: number[], pt: number[]) {
  const [a,b,c,d,e,f] = M; const [x,y] = pt;
  return [a*x + b*y + c, d*x + e*y + f];
}
/**
 * メイン：ワールドファイルを生成
 * - 自然サイズで推定した M を、実保存ピクセル寸法に合わせて補正
 * - 2点: 厳密Similarity、3点以上: ロバストAffine
 */
export function generateWorldFile (vm: VM, {
                     mode = 'canvas',     // 'original'（原画像に対するTFW/JGW）か 'canvas'（プレビューで描いた画像に対するTFW/JGW）か
                     srs  = 'EPSG:3857',    // 'EPSG:3857'（メートル） or 'EPSG:4326'（度）
                     useExportScale = false // true にすると exportWidth/Height 等の異方スケール補正を掛けます
                   } = {}) {

  const img = vm.$refs?.warpImage || document.getElementById('warp-image');
  if (!img) { console.warn('image not found'); return null; }

  // 1) GCP を取得
  const pairs = (vm.gcpList || []).filter((g: any) =>
      (Array.isArray(g.imageCoord) || Array.isArray(g.imageCoordCss)) && Array.isArray(g.mapCoord)
  );
  if (pairs.length < 2) { console.warn('GCPは2点以上必要です'); return null; }

  // 2) 画像座標（natural px, y下向き）に統一
  const toNatural = (g: any) => Array.isArray(g.imageCoord) ? g.imageCoord
      : imageCssToNatural(g.imageCoordCss, img);
  const srcNat = pairs.map(toNatural);

  // 3) 地図側を SRS に合わせて数値化
  const dstWorld = (srs === 'EPSG:4326')
      ? pairs.map((g: any) => [Number(g.mapCoord[0]), Number(g.mapCoord[1])])  // (lng,lat) 度
      : pairs.map((g: any) => lngLatToMerc(g.mapCoord));                        // 3857 m

  // 4) 原画像 → 世界の行列 M（y下→世界）
  let M: number[];
  if (pairs.length === 2) {
    M = fitSimilarity2PExactDown(srcNat[0], srcNat[1], dstWorld[0], dstWorld[1]); // 完全一致
  } else {
    const srcUp = srcNat.map(([x, y]: number[]) => [x, -y]);      // y上にして推定
    const Mup = fitAffineRobust(srcUp, dstWorld, 4);   // y上→世界
    const FLIP_Y: AffineMatrix = [1,0,0, 0,-1,0];
    M = composeAffine(Mup as AffineMatrix, FLIP_Y);                    // y下→世界
  }

  // 5) （必要時のみ）エクスポート異方スケール補正
  if (useExportScale) {
    const natW = img.naturalWidth || 1, natH = img.naturalHeight || 1;
    let sX = (Number(vm.exportWidth)  > 0 ? Number(vm.exportWidth)  : natW) / natW;
    let sY = (Number(vm.exportHeight) > 0 ? Number(vm.exportHeight) : natH) / natH;
    if (Number.isFinite(vm.pixelScaleX)) sX = Number(vm.pixelScaleX);
    if (Number.isFinite(vm.pixelScaleY)) sY = Number(vm.pixelScaleY);
    if (sX !== 1 || sY !== 1) M = composeAffine(M as AffineMatrix, [1/sX,0,0, 0,1/sY,0] as AffineMatrix); // 出力px→naturalpx
  }

  // 6) プレビュー画像（キャンバス画像）に対するワールドファイルが欲しい場合:
  //    previewOnCanvas で world→canvas に使った T を再計算し、T^{-1} を書き出す
  if (mode === 'canvas') {
    const W = img.naturalWidth, H = img.naturalHeight;
    const corners = ([[0,0],[W,0],[W,H],[0,H]] as [number,number][]).map(p => applyAffine(M as AffineMatrix, p)); // 世界座標
    const xs = corners.map(p=>p[0]), ys=corners.map(p=>p[1]);
    const minX=Math.min(...xs), maxX=Math.max(...xs), minY=Math.min(...ys), maxY=Math.max(...ys);
    // エクスポートするキャンバスの実サイズ（プレビューと同じが無難）
    const cw = img.clientWidth || W, ch = img.clientHeight || H;
    const spanX=Math.max(1,maxX-minX), spanY=Math.max(1,maxY-minY);
    const s = Math.min(cw/spanX, ch/spanY);
    // T: world → canvas
    const T = [ s, 0, -minX*s,  0, -s,  maxY*s ];
    // T^{-1}: canvas → world
    const det = T[0]*T[4] - T[1]*T[3] || 1e-12;
    const inv = [  T[4]/det, -T[1]/det, (T[1]*T[5]-T[4]*T[2])/det,
      -T[3]/det,  T[0]/det, (T[3]*T[2]-T[0]*T[5])/det ];
    // キャンバス画像ピクセル→世界 の行列が最終出力
    M = inv;
  }

  // 7) デバッグ（角度・スケール確認）

  vm._lastAffineM = M;
  vm.affineM = M;

  const wld = worldFileFromAffine(M as AffineMatrix); // 6行（A,D,B,E,C,F）
  vm.lastWorldFileText = wld;
  return wld;
}
// 受け取りに file を追加
export function onWarpConfirm(vm: VM, { kind, affineM, H, cornersLngLat, tps, blob, file, fileOriginal, maskedPngFile, worldFile }: Record<string, any>) {
  // 安全チェック（宇宙行き防止）
  if (cornersLngLat && !_validCorners(vm, cornersLngLat)) {
    console.warn('invalid corners, abort');
    alert('推定コーナーが不正です。別のGCP配置を試してください。');
    return;
  }
  if (kind === 'similarity' || kind === 'affine') {
    // ✅ アフィンは「元画像 + ワールドファイル」で登録が正道
    const worldFile = _worldFileFromAffine(vm, affineM);
    vm.openTileUploadDialog({
      kind,
      // fileOriginal: maskedPngFile,   // マスク画像を使う
      fileOriginal,
      worldFile,
      // previewBlob: blob, // （任意）プレビュー表示用に保持してもいい
    });
  } else if (kind === 'tps') {
    vm.openTileUploadDialog({
      kind,
      fileOriginal,
      tps,
      cornersLngLat, // 表示位置の初期合わせに使える
      worldFile
    });
  }
}
export function _validCorners(vm: VM, c: number[][]) {
  if (!Array.isArray(c) || c.length !== 4) return false;
  return c.every(([lng,lat]) =>
      Number.isFinite(lng) && Number.isFinite(lat) &&
      Math.abs(lng) <= 180 && Math.abs(lat) <= 89.999  // WebMercatorの有効域
  );
}
export function _worldFileFromAffine(vm: VM, [A,B,C,D,E,F]: number[]) {
  // .pgwは行順が A,D,B,E,C,F（X解像度, 回転Y, 回転X, Y解像度, 左上ピクセル中心X, 同Y）
  // 文字列で返す（アップロード先でそのまま保存）
  return `${A}\n${D}\n${B}\n${E}\n${C}\n${F}\n`;
}
export function onWarpWindowClose(vm: VM) {
  // ジオリファレンス関連のデータをすべてクリア
  clearBlueDotsOnMap(vm);
  vm.gcpList = [];
  vm.$store.commit('setPendingFile', null);
}
export function clearBlueDotsOnMap(vm: VM) {
  vm.mapCoordMarkers?.forEach((m: any) => m.remove());
  vm.mapCoordMarkers = [];
}